import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { namedOperations } from '@sorare/core/src/__generated__/operations';
import { Modal } from '@sorare/core/src/atoms/Modal';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { AnimatedIntro } from '@sorare/core/src/components/animations/AnimatedStory/AnimatedIntro';
import { HeadlinePill } from '@sorare/core/src/components/animations/AnimatedStory/HeadlinePill';
import { CardsCarousel } from '@sorare/core/src/components/landing/CollectCards/CardsCarousel';
import { useResizeObserver } from '@sorare/core/src/hooks/ui/useResizeObserver';
import { formatScarcity } from '@sorare/core/src/lib/cards';
import { glossary } from '@sorare/core/src/lib/glossary';

import { VAULT_PROGRESS_BAR_COLORS } from '../constant';
import { useUnsealingMutation } from '../useUnsealingMutation';
import type { UnsealingAnimation_anyCard } from './__generated__/index.graphql';

const cardsEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(25%);
    }
    50% {
    opacity: 0;
    }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const gameweekEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const CardsContainer = styled(Vertical)`
  padding: 0 var(--quadruple-unit);

  justify-content: center;
  align-items: center;
  animation: ${cardsEnter} 1s ease-out forwards;
  max-height: 100%;
`;

const GameweekDateWrapper = styled(Vertical).attrs({ center: true })`
  animation: ${gameweekEnter} 0.5s ease-out forwards;
`;

const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  position: fixed;
  height: 100vh;
  width: var(--100vw);
  inset: 0;
  overflow: hidden;
  background-color: var(--c-black);
`;
type Props = {
  rarity: Rarity;
  onClose: () => void;
  cards: UnsealingAnimation_anyCard[];
};
export const UnsealingAnimation = ({ rarity, cards, onClose }: Props) => {
  const [beforeSealing, setBeforeSealing] = useState(true);
  const [afterSealing, setAfterSealing] = useState(false);
  const [isUnmounting, setIsUnmounting] = useState(false);
  const [continueButtonVisible, setContinueButtonVisible] = useState(false);
  const storyRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [unsealCards, { loading: processing }] = useUnsealingMutation();

  const unsealing = useCallback(async () => {
    const unsealableCards = cards.filter(card => card.sealed);
    if (unsealableCards.length) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setBeforeSealing(false);
      await new Promise(resolve => setTimeout(resolve, 1000));
      await unsealCards({
        variables: {
          input: {
            cardSlugs: unsealableCards.map(card => card.slug),
          },
        },
        refetchQueries: [namedOperations.Query.RarityVaultBannerQuery],
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      setAfterSealing(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setContinueButtonVisible(true);
    }
  }, [cards, unsealCards]);

  useLayoutEffect(() => {
    // Use requestAnimationFrame to ensure DOM is painted
    requestAnimationFrame(() => {
      const currentHeight = storyRef.current?.getBoundingClientRect().height;
      if (currentHeight) {
        setHeight(Math.min(currentHeight, 1000));
      }
      // Start unsealing after height is set
      unsealing();
    });
  }, [unsealing]);

  useResizeObserver(storyRef, () => {
    const newHeight = storyRef.current?.getBoundingClientRect().height;
    if (newHeight) {
      setHeight(Math.min(newHeight, 1000));
    }
  });

  // Use a default height if measurement isn't ready
  const modalHeight = Math.max(height ?? 500, 1000);

  const actionAfterOutro = (action?: () => void) => {
    setIsUnmounting(true);
    if (!action) return;
    setTimeout(action, 1000);
  };

  return (
    <Modal>
      <Wrapper ref={storyRef}>
        <AnimatedIntro
          isUnmounting={isUnmounting}
          setIsUnmounting={setIsUnmounting}
          stopBg={afterSealing}
          onClose={onClose}
          headlinePill={
            <GameweekDateWrapper>
              <HeadlinePill
                colors={{ bg: VAULT_PROGRESS_BAR_COLORS[rarity] }}
                label={
                  <BrandS>
                    <FormattedMessage
                      id="Gallery.Vault.scarcityVault"
                      defaultMessage="{rarity} vault"
                      values={{ rarity: formatScarcity(rarity) }}
                    />
                  </BrandS>
                }
              />
            </GameweekDateWrapper>
          }
          buttons={
            continueButtonVisible ? (
              <Button
                size="medium"
                color="secondary"
                fullWidth
                disabled={processing}
                onClick={async () => actionAfterOutro(onClose)}
              >
                <FormattedMessage {...glossary.continue} />
              </Button>
            ) : null
          }
        >
          <CardsContainer style={{ height: modalHeight - 400 }}>
            <CardsCarousel
              cards={cards.map(card => ({
                src: proxyUrl(card.pictureUrl || '', {
                  cropWidth: 320,
                }),
                alt: `${card.anyPlayer?.displayName}${card.rarityTyped ? ` - ${card.rarityTyped}` : ''}`,
                className: classNames({
                  fadeOut: !beforeSealing && !afterSealing,
                }),
              }))}
              slotSize={(modalHeight - 400) * 0.5}
            />
          </CardsContainer>
        </AnimatedIntro>
      </Wrapper>
    </Modal>
  );
};

UnsealingAnimation.fragments = {
  anyCard: gql`
    fragment UnsealingAnimation_anyCard on AnyCardInterface {
      slug
      sealed
      rarityTyped
      pictureUrl
      anyPlayer {
        slug
        displayName
      }
    }
  ` as TypedDocumentNode<UnsealingAnimation_anyCard>,
};
